import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { fetchManagerResidentsFilter } from '../lk-manager-residents/manager-residents-api';
import { isManagerReportsMockEnabled } from './is-manager-reports-mock';
import { getMockManagerReportById, getMockManagerReportsList } from './mocks';
import { filterManagerReports } from './map-manager-report';
import type {
  ManagerReportRow,
  ManagerReportSignCheckResult,
  ManagerReportsFilter,
  ManagerReportsListData,
} from './types';

type ApiReportsResponse = {
  status?: 'success' | 'error';
  msg?: string;
  data?: ManagerReportRow[];
};

type ApiSuccessResponse<T> = {
  status: 'success';
  msg: T;
};

type ApiErrorResponse = {
  status: 'error';
  msg: string;
};

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Сервер вернул некорректный ответ');
  }
}

async function parsePersonalApi<T>(response: Response, fallbackMessage: string): Promise<T> {
  const result = await parseJsonResponse<ApiSuccessResponse<T> | ApiErrorResponse>(response);

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : fallbackMessage,
    );
  }

  return result.msg;
}

function getPersonalApiHeaders(sessid: string, withJson = true): HeadersInit {
  const headers: HeadersInit = {
    'X-Bitrix-Csrf-Token': sessid,
  };

  if (withJson) {
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

export async function fetchManagerReportsList(params: {
  q?: string;
  filter?: ManagerReportsFilter;
}): Promise<ManagerReportsListData> {
  if (isManagerReportsMockEnabled()) {
    return getMockManagerReportsList(params);
  }

  const url = new URL(getApiUrl('/api/personal/manager-report/'), window.location.origin);

  if (params.filter?.clusterId) {
    url.searchParams.set('clusterId', params.filter.clusterId);
  }

  if (params.filter?.year) {
    url.searchParams.set('year', params.filter.year);
  }

  if (params.filter?.statusId) {
    url.searchParams.set('statusId', params.filter.statusId);
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error('Не удалось загрузить список отчётов');
  }

  const data = (await response.json()) as ApiReportsResponse;
  if (data.status === 'error') {
    throw new Error(data.msg ?? 'Не удалось загрузить список отчётов');
  }

  let items = Array.isArray(data.data) ? data.data : [];

  items = filterManagerReports(items, {
    q: params.q,
    filter: params.filter,
  });

  const residentsData = await fetchManagerResidentsFilter().catch(() => null);

  return {
    items,
    clusters: residentsData?.clusters ?? [],
  };
}

export async function fetchManagerReportDetail(
  id: number,
  fallback?: ManagerReportRow,
): Promise<ManagerReportRow> {
  if (isManagerReportsMockEnabled()) {
    const report = getMockManagerReportById(id);
    if (!report) {
      throw new Error('Отчёт не найден');
    }
    return report;
  }

  if (fallback) {
    return fallback;
  }

  const data = await fetchManagerReportsList({});
  const report = data.items.find((item) => item.id === id);
  if (!report) {
    throw new Error('Отчёт не найден');
  }

  return report;
}

export async function applyManagerReport(id: number): Promise<string> {
  if (isManagerReportsMockEnabled()) {
    return 'Отчёт принят';
  }

  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const response = await fetch(getApiUrl(`/api/personal/manager-report/${id}/apply/`), {
    method: 'POST',
    credentials: 'same-origin',
    headers: getPersonalApiHeaders(sessid),
    body: JSON.stringify({ sessid }),
  });

  return parsePersonalApi<string>(response, 'Не удалось принять отчёт');
}

export async function rejectManagerReport(
  id: number,
  payload: { comment: string; files?: File[] },
): Promise<string> {
  if (isManagerReportsMockEnabled()) {
    return 'Отчёт отправлен на доработку';
  }

  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const body = new FormData();
  body.append('comment', payload.comment.trim());
  body.append('sessid', sessid);
  payload.files?.forEach((file) => body.append('attachments[]', file));

  const response = await fetch(getApiUrl(`/api/personal/manager-report/${id}/reject/`), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
    body,
  });

  return parsePersonalApi<string>(response, 'Не удалось вернуть отчёт на доработку');
}

export function getManagerReportsExportUrl(): string {
  const url = new URL(getApiUrl('/api/getExportReports.php'), window.location.origin);
  url.searchParams.set('residentId', '-1');
  return url.toString();
}

export { getManagerReportArchiveUrl, getManagerReportSignUrl } from './manager-report-urls';

export async function checkManagerReportSign(
  reportId: number,
): Promise<ManagerReportSignCheckResult> {
  if (isManagerReportsMockEnabled()) {
    return {
      verify: true,
      fio: 'Грушев Н. П.',
      nameOrganization: 'ООО «Технологии будущего»',
      certNumber: '00A1B2C3',
      certAuthority: 'Федеральное казначейство',
      dateSign: '15.03.2025 14:30:00',
      dateEndSert: '15.03.2026',
    };
  }

  const response = await fetch(getApiUrl(`/api/personal/report/${reportId}/check-sign/`), {
    credentials: 'same-origin',
  });

  return parsePersonalApi<ManagerReportSignCheckResult>(
    response,
    'Не удалось проверить подпись',
  );
}
